import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import LoadingSpinner from '../components/LoadingSpinner';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const AuthCallbackPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setTokens, setUser } = useAuthStore();
  const [error, setError] = useState<string | null>(null);
  const processedRef = useRef(false);

  useEffect(() => {
    console.log('AuthCallbackPage mounted, URL params:', window.location.search);
    console.log('searchParams:', Object.fromEntries(searchParams.entries()));
    
    if (processedRef.current) {
      console.log('Already processed, skipping');
      return;
    }
    
    const processCallback = async () => {
      const accessToken = searchParams.get('access_token');
      const needsOnboarding = searchParams.get('needs_onboarding') === 'true';
      const redirect = searchParams.get('redirect') || '/app/subscription';
      const errorParam = searchParams.get('error');
      
      console.log('processCallback called:', { accessToken: accessToken?.substring(0, 20) + '...', needsOnboarding, redirect, errorParam });

      if (errorParam) {
        setError(getErrorMessage(errorParam));
        setTimeout(() => navigate('/login'), 3000);
        return;
      }

      if (!accessToken) {
        setError('Authentication failed. Missing access token.');
        setTimeout(() => navigate('/login'), 3000);
        return;
      }

      processedRef.current = true;

      try {
        // Fetch user profile directly with the access token
        const profileResponse = await fetch(`${API_BASE_URL}/auth/profile/`, {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        });

        if (!profileResponse.ok) {
          throw new Error('Failed to fetch user profile');
        }

        const user = await profileResponse.json();

        // Store tokens and user in auth store
        setTokens({ access: accessToken, refresh: '' });
        console.log('user 54. satır', JSON.stringify(user, null, 2));
        setUser(user);

        // Navigate to the appropriate page
        const targetPath = needsOnboarding ? '/app/subscription' : redirect;
        navigate(targetPath, { replace: true });
      } catch (err) {
        console.error('Auth callback error:', err);
        setError('Failed to complete authentication. Please try again.');
        setTimeout(() => navigate('/login'), 3000);
      }
    };

    processCallback();
  }, [searchParams, setTokens, setUser, navigate]);

  const getErrorMessage = (error: string): string => {
    const errorMessages: Record<string, string> = {
      google_auth_failed: 'Google authentication was cancelled or failed.',
      no_code: 'No authorization code received from Google.',
      oauth_not_configured: 'Google OAuth is not properly configured.',
      token_exchange_failed: 'Failed to exchange authorization code.',
      no_id_token: 'No ID token received from Google.',
      no_email: 'Email was not provided by Google.',
      invalid_token: 'The authentication token is invalid.',
      auth_failed: 'Authentication failed. Please try again.',
    };
    return errorMessages[error] || 'An unknown error occurred.';
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center max-w-md px-4">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
            <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Authentication Failed
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {error}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-500">
            Redirecting to login page...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
      <div className="text-center">
        <LoadingSpinner size="large" />
        <p className="mt-4 text-gray-600 dark:text-gray-400">
          Completing sign in...
        </p>
      </div>
    </div>
  );
};

export default AuthCallbackPage;
